import { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import LoadingBar from '../components/Reusable/LoadingBar';
import { PATH_ROOT } from '../routes/paths';

// Clerk-backed GuestGuard. Replaces the Cognito-driven implementation
// 2026-04-20. The PENDING-onboarding redirect from the prior version is
// gone — Clerk's own SignUp flow handles email-verification + first-time
// setup; once Clerk says signed-in, the user is past onboarding.
type GuestGuardProps = { children: ReactNode };

export default function GuestGuard({ children }: GuestGuardProps) {
  const { isLoaded, isSignedIn } = useAuth();

  if (!isLoaded) {
    return (
      <div className="fixed inset-0 bg-[#181818] flex items-center justify-center">
        <LoadingBar />
      </div>
    );
  }

  // Escape hatch: if the URL carries ?force-signout=1, render children
  // (the Login view) even when Clerk thinks we're signed in. The Login
  // component handles awaiting Clerk.signOut() at mount, then strips the
  // query param so refreshes don't loop. Without this bypass, GuestGuard
  // would Navigate to /organizations BEFORE Login even gets a chance to
  // clear the stale Clerk session — which is exactly the loop the
  // apiClient redirect was trying to escape.
  const forceSignout = typeof window !== 'undefined'
    && new URLSearchParams(window.location.search).has('force-signout');

  if (isSignedIn && !forceSignout) {
    return <Navigate to={PATH_ROOT.organization.organizations} />;
  }

  return <>{children}</>;
}
